import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from './ui/card';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-0 transition-colors">
      <button
        onClick={onClick}
        className="w-full py-4 px-2 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="font-semibold text-gray-800 dark:text-gray-100 transition-colors">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors" />
        )}
      </button>
      {isOpen && (
        <div className="px-2 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-2 transition-colors">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How does voice logging work?",
      answer: (
        <>
          <p>Speak naturally into the microphone: <em>"Worked 2 hours on client project with Sarah"</em></p>
          <p className="mt-2">Our AI (powered by GPT-4) automatically extracts:</p>
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>Duration (2 hours)</li>
            <li>Project name (client project)</li>
            <li>Attendees (Sarah)</li>
            <li>Entry type (meeting/work)</li>
          </ul>
          <p className="mt-2">You can edit any details before saving.</p>
        </>
      )
    },
    {
      question: "How do I set project budgets?",
      answer: (
        <>
          <p><strong>Project budgets help you stay within limits:</strong></p>
          <ol className="list-decimal list-inside ml-4 mt-2 space-y-1">
            <li>Go to Projects page</li>
            <li>Create or edit a project</li>
            <li>Set <strong>Time Budget</strong> (hours) and/or <strong>Money Budget</strong></li>
            <li>Choose budget type: Fixed or Flexible</li>
            <li>System tracks automatically as you log time</li>
          </ol>
          <p className="mt-2 text-cyan-600 dark:text-cyan-400 transition-colors"><strong>Alert Thresholds:</strong> 75% (Info), 90% (Warning), 100%+ (Danger)</p>
        </>
      )
    },
    {
      question: "What are hourly rates and how do they work?",
      answer: (
        <>
          <p><strong>Set custom hourly rates per project:</strong></p>
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li>Each project can have its own hourly rate</li>
            <li>Supports 10 currencies: USD, EUR, GBP, JPY, AUD, CAD, CHF, CNY, INR, SGD</li>
            <li>Automatic earnings calculation based on logged hours</li>
            <li>Mark rates as billable or non-billable</li>
            <li>View earnings in dashboard and project cards</li>
          </ul>
          <p className="mt-2">Example: $50/hr × 10 hours = $500 earned</p>
        </>
      )
    },
    {
      question: "How do project timelines and deadlines work?",
      answer: (
        <>
          <p><strong>Track project deadlines with visual alerts:</strong></p>
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li>Set start date and due date for each project</li>
            <li>System calculates days until due automatically</li>
            <li>Color-coded alerts: 🔴 Overdue, 🟠 0-3 days, 🟡 4-7 days, 🟢 On track</li>
            <li>Deadline alerts appear on project cards and dashboard</li>
            <li>Get notified when approaching deadlines</li>
          </ul>
        </>
      )
    },
    {
      question: "How do I archive completed projects?",
      answer: (
        <>
          <p><strong>Keep your workspace clean:</strong></p>
          <ol className="list-decimal list-inside ml-4 mt-2 space-y-1">
            <li>Go to Projects page</li>
            <li>Find the completed project</li>
            <li>Click the Archive button</li>
            <li>Project moves to Archive tab</li>
            <li>Restore anytime if needed</li>
          </ol>
          <p className="mt-2 text-cyan-600 dark:text-cyan-400 transition-colors">Archived projects retain all data but don't appear in active views or reports.</p>
        </>
      )
    },
    {
      question: "What are project tags and categories?",
      answer: (
        <>
          <p><strong>Organize projects for better tracking:</strong></p>
          <p className="mt-2"><strong>Tags:</strong></p>
          <ul className="list-disc list-inside ml-4">
            <li>Add multiple keywords (e.g., "client", "urgent", "research")</li>
            <li>Filter projects by tags</li>
            <li>Great for cross-project themes</li>
          </ul>
          <p className="mt-2"><strong>Categories:</strong></p>
          <ul className="list-disc list-inside ml-4">
            <li>Group projects (e.g., "Client Work", "Internal", "Personal")</li>
            <li>Each project has one category</li>
            <li>Better reporting and organization</li>
          </ul>
        </>
      )
    },
    {
      question: "What is a default project?",
      answer: (
        <>
          <p><strong>Quick logging made easier:</strong></p>
          <p className="mt-2">Set one project as your default. When you start a timer or voice entry without specifying a project, it automatically logs to your default project.</p>
          <p className="mt-2"><strong>How to set:</strong></p>
          <ol className="list-decimal list-inside ml-4 space-y-1">
            <li>Go to Projects page</li>
            <li>Find your most-used project</li>
            <li>Click "Set as Default"</li>
            <li>Default badge appears on project card</li>
          </ol>
        </>
      )
    },
    {
      question: "Can I use Logly offline?",
      answer: (
        <>
          <p>Yes! Logly is a Progressive Web App (PWA) with offline support.</p>
          <p className="mt-2"><strong>What works offline:</strong></p>
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>View existing time entries</li>
            <li>Browse projects and calendar</li>
            <li>Access settings</li>
          </ul>
          <p className="mt-2"><strong>What needs internet:</strong></p>
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>Voice transcription (requires AI)</li>
            <li>Creating/editing entries</li>
            <li>Syncing data</li>
          </ul>
          <p className="mt-2 text-cyan-600 dark:text-cyan-400 transition-colors">Changes will sync automatically when you're back online!</p>
        </>
      )
    },
    {
      question: "How does the timer work?",
      answer: (
        <>
          <p>Start a timer for real-time tracking:</p>
          <ol className="list-decimal list-inside ml-4 mt-2 space-y-1">
            <li>Click a Quick Start button or "+ Start timer with description"</li>
            <li>Timer runs in top-right corner</li>
            <li>Pause anytime (auto-pauses after 10 min idle)</li>
            <li>Stop and add description when done</li>
            <li>Entry is saved with exact duration</li>
          </ol>
          <p className="mt-2 text-cyan-600 dark:text-cyan-400 transition-colors"><strong>Tip:</strong> Timer persists even if you close the browser!</p>
        </>
      )
    },
    {
      question: "What happens after my free trial?",
      answer: (
        <>
          <p>You get <strong>7 days free</strong> to try all features.</p>
          <p className="mt-2">After the trial:</p>
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>Subscription: <strong>$2.99/month</strong></li>
            <li>Billed monthly via Stripe</li>
            <li>Cancel anytime (no commitment)</li>
            <li>Keep all your data</li>
          </ul>
          <p className="mt-2">We'll email you 1 day before trial ends as a reminder.</p>
        </>
      )
    },
    {
      question: "How do I export my timesheets?",
      answer: (
        <>
          <p><strong>Steps:</strong></p>
          <ol className="list-decimal list-inside ml-4 mt-2 space-y-1">
            <li>Go to Settings → Export Data</li>
            <li>Select date range (From/To)</li>
            <li>Choose specific projects or "All Projects"</li>
            <li>Select format: CSV or PDF</li>
            <li>Click "Export" button</li>
          </ol>
          <p className="mt-2"><strong>PDF includes:</strong> Project breakdown, total hours, descriptions</p>
          <p className="mt-1"><strong>CSV includes:</strong> All entry details for spreadsheets</p>
        </>
      )
    },
    {
      question: "How does AI Project Predictor work?",
      answer: (
        <>
          <p>AI learns from your time tracking patterns:</p>
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li><strong>Time patterns:</strong> "Monday 9 AM = Project A"</li>
            <li><strong>Keywords:</strong> "database" → Database project</li>
            <li><strong>Attendees:</strong> "John" → Client project</li>
          </ul>
          <p className="mt-2">After ~10 entries, AI starts suggesting projects with high accuracy.</p>
          <p className="mt-2 text-cyan-600 dark:text-cyan-400 transition-colors">Toggle on/off in Settings → AI Project Predictor</p>
        </>
      )
    },
    {
      question: "Can I install Logly as a mobile app?",
      answer: (
        <>
          <p>Yes! Logly is a PWA - install it like a native app:</p>
          <p className="mt-2"><strong>iPhone (Safari):</strong></p>
          <ol className="list-decimal list-inside ml-4 space-y-1">
            <li>Tap Share button (bottom center)</li>
            <li>Scroll down → "Add to Home Screen"</li>
            <li>Tap "Add"</li>
          </ol>
          <p className="mt-2"><strong>Android (Chrome):</strong></p>
          <ol className="list-decimal list-inside ml-4 space-y-1">
            <li>Tap menu (⋮) top right</li>
            <li>Tap "Add to Home screen"</li>
            <li>Tap "Add"</li>
          </ol>
          <p className="mt-2">Logly will appear on your home screen like any other app!</p>
        </>
      )
    },
    {
      question: "How do I cancel my subscription?",
      answer: (
        <>
          <p>Cancel anytime with no penalties:</p>
          <ol className="list-decimal list-inside ml-4 mt-2 space-y-1">
            <li>Go to Settings → Subscription</li>
            <li>Click "Manage Subscription"</li>
            <li>You'll be redirected to Stripe portal</li>
            <li>Click "Cancel subscription"</li>
            <li>Confirm cancellation</li>
          </ol>
          <p className="mt-2"><strong>What happens:</strong></p>
          <ul className="list-disc list-inside ml-4 mt-1">
            <li>Access until end of billing period</li>
            <li>No future charges</li>
            <li>Data remains accessible</li>
          </ul>
        </>
      )
    },
    {
      question: "What is the Enhanced Dashboard?",
      answer: (
        <>
          <p><strong>Your new home page shows:</strong></p>
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li><strong>Metrics Grid:</strong> Hours worked, earnings, active projects, at-risk status</li>
            <li><strong>Week-over-week trends:</strong> Compare with last week</li>
            <li><strong>Project Cards:</strong> Budget progress, deadlines, alerts</li>
            <li><strong>Weekly Insights:</strong> Most productive project, needs attention</li>
            <li><strong>To-Do Summary:</strong> Upcoming tasks with due dates</li>
          </ul>
          <p className="mt-2 text-cyan-600 dark:text-cyan-400 transition-colors">Click any project card for detailed view!</p>
        </>
      )
    }
  ];

  return (
    <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100 transition-colors" style={{ fontFamily: 'Space Grotesk' }}>
          ❓ Frequently Asked Questions
        </h2>
        <div className="space-y-0">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </Card>
  );
};

export default FAQSection;
